import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Tick02Icon,
  ArrowRight01Icon,
  Calendar03Icon,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { createCreditsApi } from "@/lib/api/credits";
import { cn } from "@/lib/utils";

/* ─────────────────────── Shared Plan Data ──────────────────────── */

export interface PlanFeature {
  text: string;
  comingSoon?: boolean;
}

export interface Plan {
  name: string;
  monthlyPrice: number;
  annualPrice: number; // per month, billed annually
  description: string;
  credits: string;
  creditsNum: number;
  features: PlanFeature[];
  cta: string;
  highlighted?: boolean;
}

export const plans: Plan[] = [
  {
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "Get started with AI pipelines at no cost.",
    credits: "250 monthly credits",
    creditsNum: 250,
    features: [
      { text: "All AI models" },
      { text: "Up to 4K export" },
      { text: "3 saved workflows" },
      { text: "Community support" },
    ],
    cta: "Get Started",
  },
  {
    name: "Pro",
    monthlyPrice: 29,
    annualPrice: 24,
    description: "For creators who need more power.",
    credits: "2,500 monthly credits",
    creditsNum: 2500,
    highlighted: true,
    features: [
      { text: "Everything in Free" },
      { text: "Unlimited workflows" },
      { text: "Credit top-ups ($10 / 2,000 cr)" },
      { text: "API access", comingSoon: true },
      { text: "Custom templates", comingSoon: true },
      { text: "Priority support" },
    ],
    cta: "Go Pro",
  },
  {
    name: "Max",
    monthlyPrice: 99,
    annualPrice: 79,
    description: "For teams and power users pushing the limits.",
    credits: "12,500 monthly credits",
    creditsNum: 12500,
    features: [
      { text: "Everything in Pro" },
      { text: "Credit top-ups ($10 / 3,000 cr)" },
      { text: "Early access to new models" },
      { text: "Team collaboration", comingSoon: true },
      { text: "Dedicated support" },
      { text: "Custom integrations", comingSoon: true },
    ],
    cta: "Go Max",
  },
];

export const enterpriseFeatures = [
  "Dedicated support",
  "SSO & SCIM",
  "Custom integrations",
  "SLA guarantees",
  "Audit logs",
  "Onboarding services",
];

/* ─────────────────────── PricingCards Component ────────────────── */

export function PricingCards() {
  const navigate = useNavigate();
  const { isSignedIn, getToken } = useAuth();
  const { openSignIn } = useClerk();
  const [isAnnual, setIsAnnual] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  async function handleCtaClick(plan: Plan) {
    const planSlug = plan.name.toLowerCase() as "free" | "pro" | "max";

    if (planSlug === "free") {
      if (isSignedIn) navigate("/dashboard");
      else openSignIn({ fallbackRedirectUrl: "/dashboard" });
      return;
    }

    if (!isSignedIn) {
      openSignIn({ fallbackRedirectUrl: "/pricing" });
      return;
    }

    setLoadingPlan(plan.name);
    try {
      const api = createCreditsApi({ getToken });
      const { url } = await api.subscribe(planSlug, isAnnual ? "annual" : "monthly");
      window.location.assign(url);
    } catch {
      setLoadingPlan(null);
    }
  }

  return (
    <section className="relative pb-0! sm:pb-28">
      <div className="mx-auto max-w-6xl px-6">
        {/* Billing toggle pill */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mb-10 flex justify-center"
        >
          <div className="bg-foreground/[0.06] dark:bg-foreground/10 relative inline-flex rounded-full p-1">
            <button
              type="button"
              onClick={() => setIsAnnual(false)}
              className={cn(
                "relative z-10 cursor-pointer rounded-full px-5 py-1.5 text-sm font-medium transition-colors duration-200",
                !isAnnual
                  ? "text-foreground"
                  : "text-muted-foreground/60 hover:text-muted-foreground",
              )}
            >
              {!isAnnual && (
                <motion.span
                  layoutId="pricing-pill"
                  className="bg-background absolute inset-0 rounded-full shadow-sm ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                />
              )}
              <span className="relative">Monthly</span>
            </button>
            <button
              type="button"
              onClick={() => setIsAnnual(true)}
              className={cn(
                "relative z-10 flex cursor-pointer items-center gap-1.5 rounded-full px-5 py-1.5 text-sm font-medium transition-colors duration-200",
                isAnnual
                  ? "text-foreground"
                  : "text-muted-foreground/60 hover:text-muted-foreground",
              )}
            >
              {isAnnual && (
                <motion.span
                  layoutId="pricing-pill"
                  className="bg-background absolute inset-0 rounded-full shadow-sm ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                />
              )}
              <span className="relative">Yearly</span>
              <span className="text-brand relative text-xs font-semibold">
                (save 20%)
              </span>
            </button>
          </div>
        </motion.div>

        {/* Plan cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {plans.map((plan, i) => {
            const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
            const showSavings =
              isAnnual &&
              plan.monthlyPrice > 0 &&
              plan.monthlyPrice !== plan.annualPrice;

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.15 * i + 0.3,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={cn(
                  "bg-card relative flex flex-col rounded-2xl p-6 ring-1 transition-shadow duration-300",
                  plan.highlighted
                    ? "ring-brand/40 shadow-[0_0_40px_-8px_oklch(0.55_0.25_285_/_0.15)]"
                    : "ring-foreground/10 hover:ring-foreground/20",
                )}
              >
                {plan.highlighted && (
                  <Badge className="bg-brand text-brand-foreground absolute -top-2.5 left-6 border-none text-xs">
                    Most Popular
                  </Badge>
                )}

                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-foreground text-lg font-semibold">
                    {plan.name}
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {plan.description}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display text-foreground text-4xl font-normal tracking-tight">
                      ${price}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      /month
                    </span>
                    {showSavings && (
                      <span className="text-muted-foreground/60 ml-auto text-xs">
                        Billed annually
                      </span>
                    )}
                  </div>
                  <p className="text-brand mt-1.5 text-sm font-medium">
                    {plan.credits}
                  </p>
                </div>

                {/* CTA */}
                <Button
                  className={cn(
                    "mb-6 w-full gap-2 cursor-pointer",
                    plan.highlighted
                      ? "bg-brand text-brand-foreground hover:bg-brand/90 border-none"
                      : "bg-foreground text-background hover:bg-foreground/90 border-none",
                  )}
                  size="lg"
                  disabled={loadingPlan === plan.name}
                  onClick={() => handleCtaClick(plan)}
                >
                  {loadingPlan === plan.name ? "Redirecting…" : plan.cta}
                  {loadingPlan !== plan.name && (
                    <HugeiconsIcon
                      icon={ArrowRight01Icon}
                      className="size-4"
                      data-icon="inline-end"
                    />
                  )}
                </Button>

                {/* Features */}
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature.text}
                      className="flex items-start gap-2.5 text-sm"
                    >
                      <HugeiconsIcon
                        icon={Tick02Icon}
                        className={cn("mt-0.5 size-4 shrink-0 text-brand")}
                      />
                      <span className="text-foreground/80">
                        {feature.text}
                        {feature.comingSoon && (
                          <span className="text-muted-foreground/50"> *</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Enterprise card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
          className="bg-card mt-6 rounded-2xl p-6 ring-1 ring-foreground/10"
        >
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-foreground text-lg font-semibold">
                Enterprise
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">
                Built for large organisations needing flexibility, scale, and
                governance.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-6">
              <div>
                <span className="font-display text-foreground text-3xl font-normal tracking-tight">
                  Custom
                </span>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  Tailored to your needs
                </p>
              </div>
              <Button variant="outline" className="gap-2 cursor-pointer" size="lg">
                <HugeiconsIcon
                  icon={Calendar03Icon}
                  className="size-4"
                  data-icon="inline-start"
                />
                Book a demo
              </Button>
            </div>
          </div>

          <Separator className="my-5" />

          <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-6">
            {enterpriseFeatures.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm">
                <HugeiconsIcon
                  icon={Tick02Icon}
                  className="text-muted-foreground size-3.5 shrink-0"
                />
                <span className="text-foreground/80">{f}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.8 }}
        className="text-muted-foreground/40 mt-16 mb-4 text-center text-xs"
      >
        * These features are on our roadmap and will be available soon.
      </motion.p>
    </section>
  );
}
